const HERO_IMAGE =
  'https://images.unsplash.com/photo-1574629810360-7efbbe195018?auto=format&fit=crop&w=1600&q=80';

/** Featured guide: soccer team checklist before a match */
export const SOCCER_GAME_DAY_CHECKLIST_POST = {
  id: 'featured-soccer-game-day-checklist',
  slug: 'soccer-team-game-day-checklist',
  title: 'Soccer Team Game Day Checklist: Before You Leave for a Match',
  excerpt:
    'A printable-style checklist for rec and club teams—confirm the fixture, pack gear, set the lineup, run the warm-up, and handle match-day logistics from 24 hours out through kickoff.',
  publishedAt: '2026-07-31T17:30:00.000Z',
  coverImageUrl: HERO_IMAGE,
  authorName: 'Daily Sports Life',
  clubName: 'Match Day Desk',
  blogCategory: 'exercise',
  featured: true,
  component: 'WellnessGuideBlogArticle',
  theme: 'exercise',
  relatedPostsTitle: 'More match prep guides',
  relatedPosts: [
    {
      slug: 'open-soccer-fields-powell-dublin-lewis-center-delaware-ohio',
      title: 'Open Soccer Fields Near Powell, Dublin, Lewis Center & Delaware (Ohio)',
      blurb: 'field locations, permits, and parking for Ohio teams.',
    },
    {
      slug: 'intermediate-soccer-goalkeeper-practice-warm-up-wall-setup',
      title: 'Intermediate Goalkeeper Practice: Warm-Up, Wall Setup & Session Ideas',
      blurb: 'keeper warm-up and wall organization on set pieces.',
    },
    {
      slug: '7v7-soccer-formation-2-3-1-guide',
      title: '7v7 Soccer Formation: The 2-3-1 Guide',
      blurb: 'shape and roles if you play 7-a-side.',
    },
    {
      slug: 'soccer-field-injury-remedies-calf-hamstring',
      title: 'On-Field Remedies for Soccer Calf, Soleus & Hamstring Injuries',
      blurb: 'first aid if someone tweaks a muscle during the match.',
    },
  ],
  sections: [
    {
      type: 'stats',
      ariaLabel: 'Checklist overview',
      items: [
        { value: '5 phases', label: '24h · pack · arrive · warm-up · kickoff' },
        { value: '11v11 & 7v7', label: 'Rec & club teams' },
        { value: 'Captain + coach', label: 'Who owns what' },
        { value: 'Printable', label: 'Work through in order' },
      ],
    },
    {
      type: 'panel',
      title: 'How to use this checklist',
      paragraphs: [
        'Assign one person—captain, coordinator, or team parent—as **match-day lead**. They run through the team logistics sections. Every player works through the personal gear list.',
        'Start **24 hours before kickoff**. Match morning is for execution, not scrambling for shinguards or debating who plays left back. If your league uses a scheduling app, confirm the fixture there first—then use this list as your field backup.',
        'Adapt for your format: 7v7 teams can skip items marked **11v11 only**. Youth teams add parent consent, roster checks, and sideline behavior notes at the bottom.',
      ],
      chips: ['Rec & club soccer', 'Ohio leagues', 'Captain-friendly', 'Printable'],
    },
    {
      type: 'panel',
      title: '24 hours before — team coordinator',
      orderedList: [
        '**Confirm fixture** — opponent, kickoff time, field name, and home/away kit color. Screenshot the schedule.',
        '**Confirm player availability** — final headcount in your group chat or app. Know your minimum to play (often 7 for 11v11, 5 for 7v7).',
        '**Check field status** — rainouts, turf closures, or permit changes. Have a backup meeting point if the complex has multiple pitches.',
        '**Share directions + parking** — pin the lot, not just the park name. Note whether cleats are allowed on turf.',
        '**Set lineup draft** — formation, starting XI (or starting 7), GK, and subs order. Leave one slot flexible for late arrivals.',
        '**Assign match roles** — captain armband, wall anchor for free kicks, corner takers, PK taker if needed.',
        '**Remind kit colors** — home vs. away shirt; bring both if your league requires it.',
        '**Weather plan** — heat: extra water and earlier arrival. Cold: layers for bench. Lightning: know your league delay policy.',
      ],
    },
    {
      type: 'panel',
      title: 'Night before — pack the team bag',
      list: [
        '**Match balls** — 3 for warm-up minimum; 1 game ball if your league requires your own.',
        '**Pump + pressure gauge** — check all balls the night before.',
        '**Cones** — 8–12 for warm-up grids and shooting lines.',
        '**Pinnies / bibs** — enough for two-sided warm-up or if kit clash at the field.',
        '**First-aid kit** — ice packs, athletic tape, bandages, pain reliever (if your league allows), plastic bags for ice.',
        '**Clipboard or phone** — lineup, subs tracker, league roster if required.',
        '**Goals** — confirm whether the field supplies anchored goals or you must bring pop-ups (common on some open Ohio parks).',
        '**Cash or card** — referee fees, park permit, or turf rental if your team pays on site.',
      ],
      callout:
        'The team bag lives packed. After every match, the match-day lead restocks tape, ice packs, and pumps so the next game starts from ready—not from zero.',
    },
    {
      type: 'table',
      title: 'Every player — personal gear checklist',
      intro: 'Tick before you leave home. Missing shinguards means you do not play in most leagues.',
      headers: ['Item', 'Required', 'Notes'],
      rows: [
        ['Shinguards (under socks)', 'Yes', 'No guards = no play in most rec and USYS leagues'],
        ['Soccer cleats (turf or firm ground)', 'Yes', 'Match surface—turf shoes on artificial, FG on grass'],
        ['Team jersey + shorts + socks', 'Yes', 'Bring backup color if home/away not confirmed'],
        ['Water bottle (full)', 'Yes', '32 oz minimum for 90 min in summer'],
        ['Second water or electrolyte', 'Recommended', 'Ohio summer leagues: heat matters'],
        ['Pre-match snack', 'Recommended', 'Banana, bar, or toast 60–90 min before—nothing new on game day'],
        ['GK gloves (keepers only)', 'Yes', 'Two pairs in wet conditions'],
        ['Warm-up top / pants', 'Recommended', 'Peel off before kickoff'],
        ['Towel + spare socks', 'Optional', 'Wet turf, rain, or halftime change'],
        ['KT Tape / ankle brace', 'If needed', 'Apply 45–60 min before activity if using tape'],
        ['ID + league pass', 'If required', 'Adult leagues and some tournaments'],
      ],
    },
    {
      type: 'table',
      title: 'Match-day timeline (example for 10:00 a.m. kickoff)',
      intro: 'Shift times for afternoon or evening games. Arrive earlier for unfamiliar fields or tournaments.',
      headers: ['Time', 'Who', 'Action'],
      rows: [
        ['8:30 a.m.', 'Coordinator', 'Leave for field (allow 30+ min buffer for traffic and parking)'],
        ['9:00 a.m.', 'Whole team', 'Arrive, find correct pitch, greet ref if present'],
        ['9:05 a.m.', 'Coordinator', 'Check goals anchored, corner flags, surface hazards'],
        ['9:10 a.m.', 'Captain + coach', 'Final lineup; confirm late arrivals and sub order'],
        ['9:15 a.m.', 'Whole team', 'FIFA 11+ or team warm-up begins (see below)'],
        ['9:35 a.m.', 'GK + wall players', 'Set-piece walk-through: wall anchor, corner roles'],
        ['9:45 a.m.', 'Whole team', 'Water, final instructions, captains with ref for coin toss'],
        ['9:50 a.m.', 'Starters', 'Last toilet break; shinguards and socks checked'],
        ['10:00 a.m.', '—', 'Kickoff'],
      ],
    },
    {
      type: 'panel',
      title: 'Team warm-up checklist (15–20 minutes)',
      intro: 'Do not skip this—even veteran rec teams lose players to cold-start calf and hamstring strains in the first 15 minutes.',
      orderedList: [
        '**3 min** — Light jog around the penalty box or half field. No sprinting yet.',
        '**3 min** — Dynamic movement: leg swings, hip circles, high knees, butt kicks, lateral shuffles.',
        '**3 min** — Passing triangle: short one- and two-touch passes, build to 15-yard passes.',
        '**3 min** — Shooting line: 6–8 shots each from edge of box at 70% power.',
        '**3 min** — Defensive shape walk-through at walking pace (press trigger, line height).',
        '**2–5 min** — Small-sided rondo or 4v4 to goal — live speed, short bursts only.',
        '**GK block** — Separate handling, footwork, and 3–4 live shots at increasing speed.',
      ],
      callout:
        'Save max-effort sprints for the match. Warm-up should leave players warm and sharp—not exhausted before kickoff.',
    },
    {
      type: 'panel',
      title: 'Pre-match huddle — 60 seconds',
      list: [
        '**Formation and roles** — who starts where; one sentence on how we press or sit.',
        '**Set pieces** — who takes corners, free kicks, and who anchors the wall.',
        '**Substitution plan** — rotation order or minute targets so nobody is surprised.',
        '**Communication cue** — one word for high press, drop, or tempo change.',
        '**Sportsmanship** — shake hands, respect the ref, no arguing from the bench.',
        '**Injury protocol** — if someone feels a pop or sharp tear, they come off immediately—no heroics.',
      ],
    },
    {
      type: 'panel',
      title: 'Goalkeeper-specific checklist',
      list: [
        'Two pairs of gloves; towel to dry palms in humidity.',
        'Pre-match activation: footwork box, handling ladder, 3–4 live shots at 70–90% power.',
        'Wall anchor assigned before kickoff—defender knows left/right shift commands.',
        'Agree wall jump vs. stand strategy with the back line.',
        'Organize one corner and one free kick in warm-up so the first set piece in the match is not a surprise.',
        'Communicate loudly on crosses—"keeper" or "away" must be heard by the whole box.',
      ],
    },
    {
      type: 'panel',
      title: '11v11 only — coordinator extras',
      list: [
        'Confirm roster matches league registration (no unregistered ringers).',
        'Bench area: chairs or bags off the sideline; subs warm up behind the line.',
        'Fourth official or ref payment handled before kickoff if required.',
        'Offside line awareness drill in warm-up if your team struggles with the trap.',
        'Halftime: water, lineup tweak, 2-minute reset—not a tactics lecture.',
      ],
    },
    {
      type: 'panel',
      title: '7v7 only — coordinator extras',
      list: [
        'Confirm sub-on-the-fly rules with the ref (most 7v7 allows flying changes).',
        'Smaller squad means everyone plays—set fair rotation before kickoff.',
        'Review formation roles (2-3-1, 3-2-1, etc.) in the huddle—one switch can leave a gap.',
        'Shorter field: emphasize quick rest defense after losing the ball.',
      ],
    },
    {
      type: 'panel',
      title: 'At the field — quick safety check (2 minutes)',
      orderedList: [
        'Goals pinned or weighted—no tipping. Nets hung if required.',
        'Scan for holes, glass, or debris on the pitch.',
        'Corner flags in place (or cones if flags missing).',
        'Hydration plan visible—who brought the team cooler?',
        'Emergency contact list accessible (phone photo is fine).',
        'Know nearest bathroom and whether the field has a trainer or AED.',
      ],
    },
    {
      type: 'panel',
      title: 'After the match — team wrap-up',
      orderedList: [
        'Cool-down jog or walk 5 minutes—do not sit in the car immediately.',
        'Shake hands with opponents and thank the ref.',
        'Log result, scorers, and cards in your app or group chat.',
        'Restock team bag: balls pumped, tape replaced, ice packs refrozen.',
        'Note injuries or near-misses—follow up before the next training.',
        'Celebrate or debrief briefly; save long film review for midweek.',
      ],
    },
    {
      type: 'table',
      title: 'Who owns what — match-day roles',
      headers: ['Task', 'Primary owner', 'Backup'],
      rows: [
        ['Confirm fixture + field', 'Team coordinator', 'Captain'],
        ['Final lineup + subs', 'Coach or captain', 'Coordinator'],
        ['Team bag + first aid', 'Coordinator', 'Any rostered adult'],
        ['Warm-up leads', 'Coach or senior player', 'Captain'],
        ['Ref / fee / roster check', 'Coordinator', 'Team treasurer'],
        ['GK wall + set pieces', 'Goalkeeper', 'Wall anchor defender'],
        ['Group chat updates', 'Anyone—pick one person', 'Captain'],
        ['Post-match result entry', 'Coordinator', 'Captain'],
      ],
    },
    {
      type: 'panel',
      title: 'Common game-day mistakes',
      list: [
        '**Arriving at kickoff** — no warm-up, higher injury risk, sluggish first 20 minutes.',
        '**Wrong field** — large complexes have 4–8 pitches; pin the lot and pitch number.',
        '**Kit clash** — bring pinnies even when you are the "home" team.',
        '**One ball** — warm-up stalls; always bring three.',
        '**No sub plan** — same tired legs at 70 minutes; plan rotation at halftime.',
        '**Arguing with the ref** — card risk and lost focus; captain talks, everyone else plays.',
        '**New cleats on match day** — blisters by halftime; break in shoes at practice.',
      ],
      callout:
        'Screenshot the master gear table and timeline the night before. Match-morning brain fog is real—even experienced captains forget pumps and pinnies.',
    },
    {
      type: 'panel',
      title: 'Youth team add-ons (parents + coaches)',
      list: [
        'Confirm every player has signed waivers and emergency contact on file.',
        'Sideline behavior: one coach gives instructions; parents cheer, do not coach.',
        'Snack schedule assigned if your club rotates post-game treats.',
        'Sunscreen and shade for midday summer games in Ohio.',
        'Equal minutes goal posted before kickoff—reduces halftime drama.',
        'Photo consent respected if the club posts match pictures.',
      ],
    },
  ],
  referenceLinks: [
    {
      label: 'FIFA 11+ injury prevention warm-up',
      url: 'https://inside.fifa.com/health-and-medical/injury-prevention',
    },
    {
      label: 'US Soccer — player health & safety',
      url: 'https://www.ussoccer.com/player-health-and-safety',
    },
    {
      label: 'Ohio South USYS — league resources',
      url: 'https://www.osysa.org',
    },
  ],
};
